import React, { useState } from "react";
import {
  Drawer,
  Form,
  Input,
  InputNumber,
  Button,
  Select,
  Card,
  List,
  Space,
  Typography,
  Divider,
  Tag,
  Modal,
  message,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ShoppingCartOutlined,
} from "@ant-design/icons";
import { useProductServices } from "../hooks/useProductServices";
import { ProductService, CreateProductRequest } from "../types/productTypes";
import { useCatalogos } from "../hooks/useCatalogos";
import { formatCurrency, parseCurrency } from "../utils/facturaUtils";

const { Text } = Typography;

interface ProductSidebarProps {
  open: boolean;
  onClose: () => void;
  onSelectProduct: (product: ProductService) => void;
}

export const ProductSidebar: React.FC<ProductSidebarProps> = ({
  open,
  onClose,
  onSelectProduct,
}) => {
  const [form] = Form.useForm();
  const [editingProduct, setEditingProduct] = useState<ProductService | null>(
    null
  );
  const [showForm, setShowForm] = useState(false);

  const { products, loading, createProduct, updateProduct, deleteProduct } =
    useProductServices();
  const { catalogos } = useCatalogos();

  const handleSubmit = async (values: CreateProductRequest) => {
    try {
      if (editingProduct) {
        await updateProduct(editingProduct.id, values);
        setEditingProduct(null);
      } else {
        await createProduct(values);
      }
      form.resetFields();
      setShowForm(false);
    } catch (error) {
      message.error(
        "Error al guardar el producto" +
          (error instanceof Error ? `: ${error.message}` : "")
      );
    }
  };

  const handleEdit = (product: ProductService) => {
    setEditingProduct(product);
    form.setFieldsValue({
      codigo: product.codigo,
      descripcion: product.descripcion,
      precioUnitario: product.precioUnitario,
      unidadMedida: product.unidadMedida,
      categoria: product.categoria,
    });
    setShowForm(true);
  };

  const handleDelete = (product: ProductService) => {
    Modal.confirm({
      title: "¿Está seguro de eliminar este producto?",
      content: `Se eliminará "${product.descripcion}" permanentemente.`,
      okText: "Eliminar",
      okType: "danger",
      cancelText: "Cancelar",
      onOk: () => deleteProduct(product.id),
    });
  };

  const handleSelectProduct = (product: ProductService) => {
    onSelectProduct(product);
    message.success(`Producto "${product.descripcion}" seleccionado`);
  };

  const resetForm = () => {
    form.resetFields();
    setEditingProduct(null);
    setShowForm(false);
  };

  return (
    <Drawer
      title={
        <Space>
          <ShoppingCartOutlined />
          <span>Gestión de Productos/Servicios</span>
        </Space>
      }
      placement="right"
      width={400}
      open={open}
      onClose={onClose}
      styles={{
        body: { padding: 0 },
      }}
    >
      <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
        {/* Header Actions */}
        <div style={{ padding: "16px", borderBottom: "1px solid #f0f0f0" }}>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            block
            onClick={() => setShowForm(!showForm)}
          >
            {showForm ? "Cancelar" : "Nuevo Producto"}
          </Button>
        </div>

        {/* Product Form */}
        {showForm && (
          <Card
            size="small"
            style={{ margin: "16px", marginBottom: 0 }}
            title={editingProduct ? "Editar Producto" : "Nuevo Producto"}
          >
            <Form
              form={form}
              layout="vertical"
              onFinish={handleSubmit}
              size="small"
            >
              <Form.Item
                label="Código"
                name="codigo"
                rules={[{ required: true, message: "Código requerido" }]}
              >
                <Input placeholder="Código del producto" />
              </Form.Item>

              <Form.Item
                label="Descripción"
                name="descripcion"
                rules={[{ required: true, message: "Descripción requerida" }]}
              >
                <Input.TextArea
                  rows={2}
                  placeholder="Descripción del producto/servicio"
                />
              </Form.Item>

              <Form.Item
                label="Precio Unitario"
                name="precioUnitario"
                rules={[{ required: true, message: "Precio requerido" }]}
              >
                <InputNumber
                  style={{ width: "100%" }}
                  min={0}
                  formatter={formatCurrency}
                  parser={parseCurrency}
                  placeholder="0.00"
                />
              </Form.Item>

              <Form.Item
                label="Unidad de Medida"
                name="unidadMedida"
                rules={[{ required: true, message: "Unidad requerida" }]}
              >
                <Select
                  placeholder="Seleccionar unidad"
                  options={catalogos.unidadesMedida}
                  showSearch
                  optionFilterProp="label"
                />
              </Form.Item>

              <Form.Item label="Categoría" name="categoria">
                <Input placeholder="Categoría (opcional)" />
              </Form.Item>

              <Space style={{ width: "100%", justifyContent: "flex-end" }}>
                <Button onClick={resetForm}>Cancelar</Button>
                <Button type="primary" htmlType="submit">
                  {editingProduct ? "Actualizar" : "Crear"}
                </Button>
              </Space>
            </Form>
          </Card>
        )}

        <Divider style={{ margin: "16px 0 8px 0" }}>
          <Text strong>Productos Disponibles ({products.length})</Text>
        </Divider>

        {/* Products List */}
        <div style={{ flex: 1, overflow: "auto", padding: "0 16px" }}>
          <List
            loading={loading}
            dataSource={products}
            locale={{ emptyText: "No hay productos registrados" }}
            renderItem={(product) => (
              <List.Item
                style={{
                  border: "1px solid #f0f0f0",
                  borderRadius: "6px",
                  marginBottom: "8px",
                  padding: "12px",
                  cursor: "pointer",
                  transition: "all 0.2s",
                }}
                className="product-item"
                onClick={() => handleSelectProduct(product)}
              >
                <div style={{ width: "100%" }}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      marginBottom: "8px",
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <Text strong style={{ fontSize: "14px" }}>
                        {product.codigo}
                      </Text>
                      <br />
                      <Text style={{ fontSize: "12px", color: "#666" }}>
                        {product.descripcion}
                      </Text>
                    </div>

                    <Space size="small">
                      <Button
                        type="text"
                        size="small"
                        icon={<EditOutlined />}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEdit(product);
                        }}
                      />
                      <Button
                        type="text"
                        size="small"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(product);
                        }}
                      />
                    </Space>
                  </div>

                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Tag color="blue" style={{ margin: 0, fontSize: "11px" }}>
                      ${product.precioUnitario.toFixed(2)}
                    </Tag>

                    {product.categoria && (
                      <Tag
                        color="green"
                        style={{ margin: 0, fontSize: "10px" }}
                      >
                        {product.categoria}
                      </Tag>
                    )}
                  </div>
                </div>
              </List.Item>
            )}
          />
        </div>
      </div>

      <style>
        {`
          .product-item:hover {
            background-color: #f9f9f9 !important;
            border-color: #1890ff !important;
            transform: translateY(-1px);
            box-shadow: 0 2px 8px rgba(0,0,0,0.1);
          }
        `}
      </style>
    </Drawer>
  );
};
